import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText } from '../../../styling/stylingHelper';
import { useAuth } from '../../../services/authentication/authContext';
import { useSubscription } from '../../../hooks/useSubscription';
import { canAccessTour, getTour } from '../../onboarding/tours';
import { getAccessibleGuides, getGuideReadingMinutes, SupportGuideArticle } from './supportGuideRegistry';

export default function SupportGuidesPage() {
  const { t } = useTranslation('supportGuides');
  const { backendUser, accountSettings } = useAuth();
  const { isActive } = useSubscription();
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('all');
  const accessibleGuides = getAccessibleGuides(backendUser, accountSettings, isActive);
  const groups = ['all', ...(['overview', 'appPages', 'account'] as const)
    .filter(value => accessibleGuides.some(guide => guide.group === value))];
  const selectedGroup = groups.includes(group) ? group : 'all';
  const query = search.trim().toLocaleLowerCase();
  const guides = accessibleGuides.filter(guide => {
    if (selectedGroup !== 'all' && guide.group !== selectedGroup) return false;
    const article = t(guide.id, { returnObjects: true }) as unknown as SupportGuideArticle;
    return [article.title, article.summary, article.sections.where, ...article.sections.what,
      article.sections.important.intro, ...article.sections.important.items]
      .join(' ').replace(/\*\*/g, '').toLocaleLowerCase().includes(query);
  });

  return <main className="mx-auto w-full max-w-7xl p-4 pb-24 sm:p-8 sm:pb-24 lg:pb-8 text-text dark:text-text-dark">
    <header className="flex items-center gap-2">
      <Link to="/support" aria-label={t('_ui.back')} className={cn('flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center', primaryText)}><ArrowLeft size={24} aria-hidden="true" /></Link>
      <h1 className="text-2xl sm:text-3xl font-bold">{t('_ui.title')}</h1>
    </header>
    <p className="mt-2 text-muted dark:text-muted-dark">{t('_ui.subtitle')}</p>
    <div role="group" aria-label={t('_ui.filter')} className="mt-6 flex overflow-x-auto rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark shadow-sm">
      {groups.map(value => <button key={value} type="button" aria-pressed={selectedGroup === value} onClick={() => setGroup(value)}
        className={`min-h-[48px] shrink-0 border-r border-border dark:border-border-dark px-5 py-3 text-sm font-medium last:border-r-0 sm:flex-1 sm:text-base ${selectedGroup === value ? 'bg-cardLightGreen dark:bg-cardLightGreen-dark text-textGreen dark:text-textGreen-dark' : 'text-muted dark:text-muted-dark hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark'}`}>
        {t(value === 'all' ? '_ui.all' : `_ui.groups.${value}`)}
      </button>)}
    </div>
    <label className="mt-5 block">
      <span className="sr-only">{t('_ui.search')}</span>
      <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('_ui.search')} className="w-full rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark p-3 text-text dark:text-text-dark placeholder:text-muted dark:placeholder:text-muted-dark" />
    </label>
    {!guides.length && <p className="mt-6 text-muted dark:text-muted-dark" role="status">{t('_ui.empty')}</p>}
    <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
      {guides.map(guide => {
        const article = t(guide.id, { returnObjects: true }) as unknown as SupportGuideArticle;
        const tour = guide.tourId ? getTour(guide.tourId) : undefined;
        const hasTour = Boolean(tour && canAccessTour(tour, backendUser, accountSettings));
        return <Link key={guide.id} to={`/support/guides/${guide.id}`} className="flex min-w-0 flex-col rounded-xl border border-border dark:border-border-dark bg-card dark:bg-card-dark p-5 hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark">
          <div className="mb-5 flex items-center justify-between gap-3">
            <BookOpen className="h-12 w-12 rounded-xl bg-cardLightGreen dark:bg-cardLightGreen-dark p-3 text-textGreen dark:text-textGreen-dark" aria-hidden="true" />
            <span className="shrink-0 rounded-full border border-border dark:border-border-dark px-3 py-1 text-xs text-muted dark:text-muted-dark">{t('_ui.readingTime', { count: getGuideReadingMinutes(article) })}</span>
          </div>
          <h2 className="text-lg font-semibold">{article.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted dark:text-muted-dark">{article.summary}</p>
          <div className="mt-auto flex items-end justify-between gap-3 pt-4 text-textGreen dark:text-textGreen-dark">
            {hasTour && <span className="text-xs">{t('_ui.walkthrough')}</span>}
            <ArrowRight size={18} className="ml-auto shrink-0" aria-hidden="true" />
          </div>
        </Link>;
      })}
    </div>
  </main>;
}
